import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { FileSignature, Plus, Search } from 'lucide-react'
import type { ProposalDto } from '@lib/dto/proposal.dto'
import type { ProposalVersionStatus } from '@lib/enums/proposal-version-status.enum'
import { formatDate } from '@lib/helpers/format'
import { formatMoney, formatMoneyAmount, parseMoney } from '@lib/helpers/money'
import { useClients } from '@/hooks/useClients'
import { useDebounced } from '@/hooks/useDebounced'
import { useProposals } from '@/hooks/useProposals'
import { useProposalFollowUps } from '@/hooks/useProposalActivity'
import { useSeasonReminders } from '@/hooks/useSeasonReminders'
import { ProposalStatusBadge } from '@/components/ClientBadges'
import { ProposalFollowUpTable } from '@/components/ProposalFollowUps'
import { SeasonReminderTable } from '@/components/SeasonReminders'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

/**
 * Every proposal across all clients, as a list of what to do next.
 *
 * Each row summarizes its LATEST version, because "where does this proposal
 * stand" is a question about the live revision — the full history is one click
 * away on the detail page. Rows are grouped by that stage, in the order the
 * work goes: drafts to finish and send, bids waiting on an answer, then the
 * answers. Deferred work from accepted proposals is its own view, since it is
 * next season's selling rather than this week's.
 */
type ProposalsView = 'proposals' | 'coming-up' | 'deferred'

/** The view named in the URL, so a reminder email can link straight to it. */
function toView(value: string | null): ProposalsView {
  if (value === 'deferred' || value === 'coming-up') return value
  return 'proposals'
}

export function ProposalsPage() {
  const [params, setParams] = useSearchParams()
  const view = toView(params.get('view'))
  const followUps = useProposalFollowUps()
  const reminders = useSeasonReminders()
  const [choosingClient, setChoosingClient] = React.useState<boolean>(false)

  function showView(next: ProposalsView): void {
    setParams(next === 'proposals' ? {} : { view: next }, {
      replace: true,
    })
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Proposals</h1>
          <p className="mt-1 text-sm text-muted-text">
            Where every proposal stands, and what to do next.
          </p>
        </div>
        <Button
          onClick={() => {
            setChoosingClient(true)
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          New proposal
        </Button>
      </header>

      <div
        role="tablist"
        aria-label="Proposal views"
        className="inline-flex rounded-md border border-default bg-surface p-0.5"
      >
        <ViewTab
          selected={view === 'proposals'}
          onSelect={() => {
            showView('proposals')
          }}
        >
          Proposals
        </ViewTab>
        <ViewTab
          selected={view === 'coming-up'}
          onSelect={() => {
            showView('coming-up')
          }}
        >
          Coming up
          {reminders.data === undefined || reminders.data.length === 0 ? null : (
            <span className="ml-1.5 text-muted-text tabular-nums">
              {reminders.data.length}
            </span>
          )}
        </ViewTab>
        <ViewTab
          selected={view === 'deferred'}
          onSelect={() => {
            showView('deferred')
          }}
        >
          Deferred work
          {followUps.data === undefined || followUps.data.length === 0 ? null : (
            <span className="ml-1.5 text-muted-text tabular-nums">
              {followUps.data.length}
            </span>
          )}
        </ViewTab>
      </div>

      {view === 'deferred' ? <DeferredWork /> : null}
      {view === 'coming-up' ? <ComingUp /> : null}
      {view === 'proposals' ? <ProposalList /> : null}

      {choosingClient ? (
        <ChooseClientDialog
          onClose={() => {
            setChoosingClient(false)
          }}
        />
      ) : null}
    </div>
  )
}

function ViewTab({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        'rounded-sm px-3 py-1 text-sm transition-colors',
        selected
          ? 'bg-subtle font-medium text-primary-text'
          : 'text-secondary-text hover:text-primary-text',
      )}
    >
      {children}
    </button>
  )
}

function ProposalList() {
  const [search, setSearch] = React.useState<string>('')
  const [includeArchived, setIncludeArchived] = React.useState<boolean>(false)
  const debouncedSearch = useDebounced(search, 250)

  const proposals = useProposals({
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    includeArchived,
  })

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-text" />
          <Input
            className="pl-8"
            placeholder="Search by title or client"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-text">
          <input
            type="checkbox"
            className="rounded border-default"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Show archived
        </label>
      </div>

      <ProposalTable
        isPending={proposals.isPending}
        isError={proposals.isError}
        proposals={proposals.data?.proposals ?? []}
        hasSearch={debouncedSearch !== ''}
        grouped
      />

      {proposals.data ? (
        <p className="text-xs text-muted-text">
          {proposals.data.total} proposal
          {proposals.data.total === 1 ? '' : 's'}
        </p>
      ) : null}
    </>
  )
}

/**
 * Accepted proposals that left work for later: next season's quotes.
 *
 * The deferred trees are already surveyed and priced, so each row is a sale
 * waiting for its budget year.
 */
function DeferredWork() {
  const followUps = useProposalFollowUps()
  if (followUps.isPending) {
    return <TableSkeleton />
  }
  if (followUps.isError) {
    return (
      <EmptyState title="Could not load deferred work">
        Refresh the page to try again.
      </EmptyState>
    )
  }
  if (followUps.data.length === 0) {
    return (
      <EmptyState title="No deferred work">
        When a client accepts part of a proposal, the work they leave for later
        is listed here to follow up next season.
      </EmptyState>
    )
  }
  return <ProposalFollowUpTable followUps={followUps.data} />
}

/**
 * Work accepted proposals estimated for a season that is close: each one to
 * rebid as a proposal of its own, or dismiss (docs/seasonal-work.md).
 */
function ComingUp() {
  const reminders = useSeasonReminders()
  if (reminders.isPending) {
    return <TableSkeleton />
  }
  if (reminders.isError) {
    return (
      <EmptyState title="Could not load what is coming up">
        Refresh the page to try again.
      </EmptyState>
    )
  }
  if (reminders.data.length === 0) {
    return (
      <EmptyState title="Nothing coming up">
        When an accepted proposal estimated work for a later season, it is
        listed here shortly before that season starts, to rebid.
      </EmptyState>
    )
  }
  return <SeasonReminderTable reminders={reminders.data} />
}

/**
 * Pick the client a new proposal is for.
 *
 * A proposal belongs to a client and covers its sites, so the form needs one
 * before it can offer anything. Asking here, rather than sending somebody off
 * to find the client first, keeps "New proposal" where people look for it.
 */
function ChooseClientDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [search, setSearch] = React.useState<string>('')
  const debouncedSearch = useDebounced(search, 200)
  const clients = useClients(debouncedSearch ? { search: debouncedSearch } : {})
  const list = clients.data?.clients ?? []

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New proposal</DialogTitle>
          <DialogDescription>Who is it for?</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-text" />
          <Input
            autoFocus
            aria-label="Search clients"
            className="pl-8"
            placeholder="Search clients"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <ul className="max-h-72 divide-y divide-default overflow-y-auto rounded-md border border-default">
          {clients.isPending ? (
            <li className="px-3 py-2 text-sm text-muted-text">Loading…</li>
          ) : null}
          {!clients.isPending && list.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-text">
              No clients match.
            </li>
          ) : null}
          {list.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => {
                  navigate(`/clients/${client.id}/proposals/new`)
                }}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-subtle"
              >
                <span className="font-medium">{client.name}</span>
                <span className="text-xs text-muted-text">
                  {client.siteCount} {client.siteCount === 1 ? 'site' : 'sites'}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-text">
          Not listed?{' '}
          <Link to="/clients/new" className="text-brand hover:underline">
            Add a new client
          </Link>{' '}
          first.
        </p>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The stage a proposal is at, from its latest version's status.
 *
 * Archived proposals are a stage of their own, last, with no total: a bid
 * somebody put away is not waiting on anyone, and adding it to "Waiting on
 * the client" would inflate the one figure that group exists to show.
 */
type Stage = 'toSend' | 'waiting' | 'accepted' | 'declined' | 'archived'

const STAGES: { stage: Stage; label: string }[] = [
  { stage: 'toSend', label: 'To send' },
  { stage: 'waiting', label: 'Waiting on the client' },
  { stage: 'accepted', label: 'Accepted' },
  { stage: 'declined', label: 'Declined' },
  { stage: 'archived', label: 'Archived' },
]

function stageOf(proposal: ProposalDto): Stage {
  if (proposal.archivedAt !== null) return 'archived'
  return stageOfStatus(proposal.latestStatus)
}

function stageOfStatus(status: ProposalVersionStatus | null): Stage {
  switch (status) {
    case 'SENT':
    case 'EXPIRED':
    case 'SUPERSEDED':
      return 'waiting'
    case 'ACCEPTED':
      return 'accepted'
    case 'DECLINED':
      return 'declined'
    case 'DRAFT':
    case null:
      return 'toSend'
  }
}

/** The total of a group's amounts, or null when none of them has one. */
function totalOf(proposals: ProposalDto[]): string | null {
  const amounts = proposals
    .map((proposal) => parseMoney(proposal.amount))
    .filter((cents): cents is number => cents !== null)
  if (amounts.length === 0) return null
  return formatMoney(amounts.reduce((sum, cents) => sum + cents, 0))
}

interface ProposalTableProps {
  isPending: boolean
  isError: boolean
  proposals: ProposalDto[]
  hasSearch: boolean
  /** Group rows by stage, with a heading and total over each group. */
  grouped?: boolean
  /** Off on a client's own page, where every row is that client. */
  showClient?: boolean
}

/** Shown wherever a list of proposals is — the page and the client detail. */
export function ProposalTable({
  isPending,
  isError,
  proposals,
  hasSearch,
  grouped = false,
  showClient = true,
}: ProposalTableProps) {
  if (isPending) {
    return <TableSkeleton />
  }

  if (isError) {
    return (
      <EmptyState title="Could not load proposals">
        Refresh the page to try again.
      </EmptyState>
    )
  }

  if (proposals.length === 0) {
    return hasSearch ? (
      <EmptyState title="No proposals match that search">
        Try a different title or client name.
      </EmptyState>
    ) : (
      <EmptyState title="No proposals yet">
        Start one with New proposal and it will show up here.
      </EmptyState>
    )
  }

  const columnCount = showClient ? 7 : 6

  function renderRows(rows: ProposalDto[]): React.ReactNode {
    return rows.map((proposal) => (
      <ProposalRow
        key={proposal.id}
        proposal={proposal}
        showClient={showClient}
      />
    ))
  }

  return (
    <div className={TABLE_FRAME}>
      <table className={TABLE}>
        <thead>
          <tr className="border-b border-default text-secondary-text">
            <th className={cn(TH, COL_STICKY, 'bg-surface')}>Proposal</th>
            {showClient ? (
              <th className={cn(TH, COL_SECONDARY)}>Client</th>
            ) : null}
            <th className={TH}>Status</th>
            <th className={TH_RIGHT} title="Tax included">
              Amount
            </th>
            <th className={cn(TH, COL_SECONDARY)}>Sent</th>
            <th className={cn(TH, COL_SECONDARY)}>Last opened</th>
            <th className={cn(TH, COL_OPTIONAL)}>Sites</th>
          </tr>
        </thead>
        <tbody>
          {grouped
            ? STAGES.map(({ stage, label }) => {
                const rows = proposals.filter(
                  (proposal) => stageOf(proposal) === stage,
                )
                if (rows.length === 0) return null
                const total = stage === 'archived' ? null : totalOf(rows)
                return (
                  <React.Fragment key={stage}>
                    <tr className="border-b border-default bg-subtle">
                      <th
                        scope="rowgroup"
                        colSpan={columnCount}
                        className="px-3 py-1.5 text-left text-xs font-medium text-secondary-text"
                      >
                        {label}
                        <span className="ml-1.5 font-normal text-muted-text">
                          {rows.length}
                          {total === null ? '' : ` · ${total}`}
                        </span>
                      </th>
                    </tr>
                    {renderRows(rows)}
                  </React.Fragment>
                )
              })
            : renderRows(proposals)}
        </tbody>
      </table>
    </div>
  )
}

function ProposalRow({
  proposal,
  showClient,
}: {
  proposal: ProposalDto
  showClient: boolean
}) {
  return (
    <tr
      className={cn(
        ROW,
        'border-b border-default last:border-b-0',
        proposal.archivedAt ? 'opacity-55' : null,
      )}
    >
      <td className={cn(TD, COL_STICKY)}>
        <Link
          to={`/proposals/${proposal.id}`}
          className="font-medium hover:text-brand hover:underline"
        >
          {proposal.title}
        </Link>
        {proposal.archivedAt ? (
          <span className="ml-2 text-xs text-muted-text">Archived</span>
        ) : null}
      </td>
      {showClient ? (
        <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
          <Link
            to={`/clients/${proposal.clientId}`}
            className="hover:text-brand hover:underline"
          >
            {proposal.clientName}
          </Link>
        </td>
      ) : null}
      <td className={TD}>
        {proposal.latestStatus ? (
          <span className="inline-flex items-center gap-1.5">
            <ProposalStatusBadge status={proposal.latestStatus} />
            {proposal.latestVersionNumber > 1 ? (
              <span className="text-xs text-muted-text tabular-nums">
                v{proposal.latestVersionNumber}
              </span>
            ) : null}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className={cn(TD_RIGHT, 'tabular-nums')}>
        {proposal.amount === null ? (
          <span className="text-muted-text">—</span>
        ) : (
          formatMoneyAmount(proposal.amount)
        )}
      </td>
      <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
        {proposal.latestSentAt ? formatDate(proposal.latestSentAt) : '—'}
      </td>
      <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
        <LastOpened proposal={proposal} />
      </td>
      {/* Site lists are the one cell allowed to wrap: a proposal spanning
          three parks is a long string, and nowrap would make it set the whole
          table's width. */}
      <td
        className={cn(
          TD,
          COL_OPTIONAL,
          'max-w-xs whitespace-normal text-secondary-text',
        )}
      >
        {proposal.siteNames.length === 0 ? '—' : proposal.siteNames.join(', ')}
      </td>
    </tr>
  )
}

/**
 * When the client last looked, or that they have not. "Not opened" is only
 * said of a link that went out: a proposal handed over in person has nothing
 * to open, and calling that unopened would send somebody chasing nothing.
 */
function LastOpened({ proposal }: { proposal: ProposalDto }) {
  if (proposal.lastViewedAt !== null) {
    return <>{formatDate(proposal.lastViewedAt)}</>
  }
  if (proposal.wasEmailed && proposal.latestStatus === 'SENT') {
    return <span className="text-warning">Not opened</span>
  }
  return <>—</>
}

function TableSkeleton() {
  return (
    <div className="rounded-lg border border-default bg-surface">
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          className="flex items-center gap-4 border-b border-default px-3 py-2 last:border-b-0"
        >
          {[0, 1, 2, 3].map((column) => (
            <div
              key={column}
              className="h-3.5 flex-1 animate-pulse rounded bg-subtle"
            />
          ))}
        </div>
      ))}
    </div>
  )
}

interface EmptyStateProps {
  title: string
  children: React.ReactNode
}

function EmptyState({ title, children }: EmptyStateProps) {
  return (
    <div className="rounded-lg border border-default bg-surface px-3 py-12 text-center">
      <FileSignature className="mx-auto h-6 w-6 text-disabled-text" />
      <p className="mt-3 text-sm font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-text">{children}</p>
    </div>
  )
}
